import { FormEvent, useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Pencil, Plus, School, TriangleAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import { NotificationModal } from "@/components/modals";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import { autoOpenSchoolId } from "@/utils/sessionBadges";
import { api, del, errorMessage, post, put } from "@/lib/api";
import useFloatRowInView, { floatRowStyle } from "@/hooks/useFloatRowInView";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import "./Schools.css";

const SCHOOL_DIALOG_STYLE: CSSProperties = { width: "min(34rem, 100%)" };

/** Extra room once similar UIS groups appear: banner + at least one match. */
const SCHOOL_MATCH_DIALOG_STYLE: CSSProperties = {
  width: "min(34rem, 100%)",
  maxHeight: "min(90vh, 52rem)",
};

interface SchoolRow {
  id: string;
  name: string;
  description?: string | null;
  display_label?: string | null;
  group_type?: string | null;
}

interface GroupMatch {
  id: string;
  name: string;
  description?: string | null;
  display_label?: string | null;
  group_type?: string | null;
  alreadySchool?: boolean;
}

export default function Schools() {
  const navigate = useNavigate();
  const { capabilities, pageAccess, session, loading: authLoading } = useAuth();
  const { toast, notice } = useNotification();
  const canCreate = Boolean(capabilities?.createSchools);
  const canEdit = Boolean(capabilities?.editSchools);
  const canRemove = Boolean(capabilities?.removeSchools);
  const canOpen = Boolean(pageAccess?.schools || canCreate || canEdit || canRemove || capabilities?.addUsers || capabilities?.viewUsers);
  const openSchoolId = autoOpenSchoolId(session?.acuityLearn);

  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [matches, setMatches] = useState<GroupMatch[] | null>(null);
  const [pendingName, setPendingName] = useState("");
  const [pendingDescription, setPendingDescription] = useState("");
  const [editing, setEditing] = useState<SchoolRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const matchesPanelRef = useRef<HTMLDivElement | null>(null);
  const schoolListRef = useRef<HTMLDivElement | null>(null);
  const addSchoolSlotRef = useRef<HTMLDivElement | null>(null);
  const showGhost = canCreate && !showForm && !loading;
  const pinAddSchool = useFloatRowInView(schoolListRef, addSchoolSlotRef, showGhost, schools.length);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const envelope = await api<{ success: boolean; data: SchoolRow[] }>("/api/schools");
      setSchools(Array.isArray(envelope.data) ? envelope.data : []);
    } catch (err) {
      notice({
        type: "error",
        title: "Could not load schools",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  }, [notice]);

  useEffect(() => {
    if (authLoading || !canOpen) return;
    if (openSchoolId) {
      navigate(`/schools/${encodeURIComponent(openSchoolId)}`, { replace: true });
      return;
    }
    void load();
  }, [authLoading, canOpen, openSchoolId, load, navigate]);

  useEffect(() => {
    if (!matches?.length) return;
    matchesPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [matches]);

  if (authLoading) {
    return <LoadingOverlay isVisible message="Loading…" />;
  }

  if (!canOpen) {
    return <Navigate to="/" replace state={accessDeniedNavigateState("schools")} />;
  }

  if (openSchoolId) {
    return <LoadingOverlay isVisible message="Loading school…" />;
  }

  const resetCreate = () => {
    setName("");
    setDescription("");
    setMatches(null);
    setPendingName("");
    setPendingDescription("");
  };

  const closeCreateForm = () => {
    resetCreate();
    setShowForm(false);
  };

  const openCreate = () => {
    if (saving) return;
    setEditing(null);
    setShowForm(true);
  };

  const finishCreate = async (body: {
    name: string;
    description: string | null;
    groupId?: string;
    createNew?: boolean;
  }) => {
    setSaving(true);
    try {
      const envelope = await post<{
        success: boolean;
        needsSelection?: boolean;
        data?: {
          matches?: GroupMatch[];
          name?: string;
          created?: boolean;
          school?: SchoolRow;
        };
      }>("/api/schools", body);
      if (envelope.needsSelection) {
        setMatches(envelope.data?.matches || []);
        setPendingName(envelope.data?.name || body.name);
        setPendingDescription(body.description || "");
        return;
      }
      toast.success(envelope.data?.created ? "School created" : "School linked");
      closeCreateForm();
      await load();
    } catch (err) {
      notice({
        type: "error",
        title: "Could not save school",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSubmit = async (event: FormEvent) => {
    event.preventDefault();
    await finishCreate({ name: name.trim(), description: description.trim() || null });
  };

  const closeEdit = () => {
    if (saving) return;
    setEditing(null);
  };

  const handleEditSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing || !editName.trim()) return;
    setSaving(true);
    try {
      await put(`/api/schools/${encodeURIComponent(editing.id)}`, {
        name: editName.trim(),
        description: editDescription.trim() || null,
        display_label: "School",
      });
      toast.success("School updated");
      setEditing(null);
      await load();
    } catch (err) {
      notice({
        type: "error",
        title: "Could not update school",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (school: SchoolRow) => {
    if (
      !window.confirm(
        `Remove “${school.name}” from AcuityLearn?\n\nThis only unmarks Appear As School in UIS. The group stays.`,
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      await del(`/api/schools/${encodeURIComponent(school.id)}`);
      toast.success("School removed from AcuityLearn");
      await load();
    } catch (err) {
      notice({
        type: "error",
        title: "Could not remove school",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="al-schools">
      <LoadingOverlay isVisible={loading} message="Loading schools…" />
      <header className="al-schools-header">
        <div className="al-schools-header-text">
          <h1>Schools</h1>
          <p className="al-schools-sub">
            Groups marked Appear As School in UIS. Create, edit, and remove are separate permissions.
          </p>
        </div>
      </header>

      {loading ? null : schools.length === 0 && !showGhost ? (
        <div className="al-schools-empty">
          <School aria-hidden="true" size={28} />
          <h2>No schools yet</h2>
          <p>Add one to get started — link an existing UIS group or create a new organization marked Appear As School.</p>
        </div>
      ) : (
        <div className="al-schools-list" ref={schoolListRef}>
          {schools.map((school) => {
            const hasActions = canEdit || canRemove;
            return (
              <article
                key={school.id}
                className={`al-schools-card al-schools-card--clickable${hasActions ? " al-schools-card--actions" : ""}`}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/schools/${encodeURIComponent(school.id)}`)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/schools/${encodeURIComponent(school.id)}`);
                  }
                }}
              >
                <div className="al-schools-cover" aria-hidden="true">
                  <School size={15} />
                </div>
                <div className="al-schools-card-body">
                  <h2 className="al-schools-card-title">{school.name}</h2>
                  <p className="al-schools-card-meta">{school.display_label || school.group_type || "School"}</p>
                  {school.description ? <p className="al-schools-card-desc">{school.description}</p> : null}
                </div>
                {hasActions ? (
                  <div
                    className="al-schools-card-tools"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    {canEdit ? (
                      <button
                        type="button"
                        className="al-schools-action-edit"
                        aria-label={`Edit ${school.name}`}
                        title="Edit name or description"
                        disabled={saving}
                        onClick={() => {
                          setEditing(school);
                          setEditName(school.name || "");
                          setEditDescription(school.description || "");
                        }}
                      >
                        <Pencil size={13} aria-hidden="true" />
                      </button>
                    ) : null}
                    {canRemove ? (
                      <button
                        type="button"
                        className="al-schools-action-delete"
                        aria-label={`Remove ${school.name} (double-click to confirm)`}
                        title="Double-click to remove"
                        disabled={saving}
                        onDoubleClick={() => void handleRemove(school)}
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
          {showGhost ? (
            <div
              ref={addSchoolSlotRef}
              className="al-schools-ghost-slot"
              style={pinAddSchool ? { height: pinAddSchool.height } : undefined}
            >
              <button
                type="button"
                className={`al-schools-ghost-row${pinAddSchool ? " is-float" : ""}`}
                style={floatRowStyle(pinAddSchool)}
                disabled={saving}
                onClick={openCreate}
              >
                <span className="al-schools-cover" aria-hidden="true">
                  <Plus size={15} />
                </span>
                <span className="al-schools-ghost-row-label">Add school</span>
              </button>
            </div>
          ) : null}
        </div>
      )}

      <NotificationModal
        isOpen={showForm && canCreate}
        type={matches?.length ? "warning" : "neutral"}
        icon={matches?.length ? <TriangleAlert /> : <Plus />}
        title={matches?.length ? "Similar group found" : "New school"}
        message={null}
        overlay
        overlayClose={!saving}
        escapeClose={!saving}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={matches?.length ? SCHOOL_MATCH_DIALOG_STYLE : SCHOOL_DIALOG_STYLE}
        onClose={() => {
          if (!saving) closeCreateForm();
        }}
      >
        <form className="al-schools-dialog-form" onSubmit={(event) => void handleCreateSubmit(event)}>
          <label className="al-schools-label" htmlFor="school-name">
            School name *
          </label>
          <input
            id="school-name"
            className="al-schools-input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. Riverside Academy"
            required
            disabled={saving || Boolean(matches)}
            autoFocus
          />
          <label className="al-schools-label" htmlFor="school-desc">
            Description
          </label>
          <textarea
            id="school-desc"
            className="al-schools-textarea"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Brief description…"
            rows={3}
            disabled={saving || Boolean(matches)}
          />
          <div className="al-schools-form-actions">
            <button type="button" className="al-schools-btn al-schools-btn--ghost" disabled={saving} onClick={closeCreateForm}>
              Cancel
            </button>
            {name.trim() && !matches?.length ? (
              <button type="submit" className="al-schools-btn" disabled={saving || Boolean(matches)}>
                {saving ? "Working…" : "Continue"}
              </button>
            ) : null}
          </div>
        </form>
        {matches?.length ? (
          <div className="al-schools-matches" role="status" ref={matchesPanelRef}>
            <div className="al-schools-caution">
              <TriangleAlert className="al-schools-caution-icon" size={18} aria-hidden="true" />
              <div>
                <p className="al-schools-caution-title">A UIS group with a similar name already exists</p>
                <p className="al-schools-caution-body">
                  If this is the same school, <strong>Link</strong> it. Creating another group with
                  this name will duplicate the organization in UIS.
                </p>
              </div>
            </div>
            {matches.map((match) => (
              <div key={match.id} className="al-schools-match">
                <div>
                  <p className="al-schools-match-name">{match.name}</p>
                  <p className="al-schools-match-meta">
                    {match.alreadySchool ? "Already an AcuityLearn school" : match.display_label || match.group_type || "UIS group"}
                  </p>
                </div>
                <button
                  type="button"
                  className="al-schools-btn"
                  disabled={saving || match.alreadySchool}
                  onClick={() =>
                    void finishCreate({
                      name: pendingName,
                      description: pendingDescription || null,
                      groupId: match.id,
                    })
                  }
                >
                  Link
                </button>
              </div>
            ))}
            <div className="al-schools-matches-else">
              <p>
                If none of the groups above match the school you are trying to add, click Create new
                anyway.
              </p>
              <button
                type="button"
                className="al-schools-btn al-schools-btn--ghost"
                disabled={saving}
                onClick={() =>
                  void finishCreate({
                    name: pendingName,
                    description: pendingDescription || null,
                    createNew: true,
                  })
                }
              >
                Create new anyway
              </button>
            </div>
          </div>
        ) : null}
      </NotificationModal>

      <NotificationModal
        isOpen={Boolean(editing) && canEdit}
        type="neutral"
        icon={<School />}
        title="Edit school"
        message={null}
        overlay
        overlayClose={!saving}
        escapeClose={!saving}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={SCHOOL_DIALOG_STYLE}
        onClose={closeEdit}
      >
        <form className="al-schools-dialog-form" onSubmit={(event) => void handleEditSave(event)}>
          <label className="al-schools-label" htmlFor="edit-school-name">
            School name
          </label>
          <input
            id="edit-school-name"
            className="al-schools-input"
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
            required
            disabled={saving}
            autoFocus
          />
          <label className="al-schools-label" htmlFor="edit-school-desc">
            Description
          </label>
          <textarea
            id="edit-school-desc"
            className="al-schools-textarea"
            value={editDescription}
            onChange={(event) => setEditDescription(event.target.value)}
            placeholder="Optional — what this school is"
            rows={3}
            disabled={saving}
          />
          <div className="al-schools-form-actions">
            <button type="button" className="al-schools-btn al-schools-btn--ghost" disabled={saving} onClick={closeEdit}>
              Cancel
            </button>
            <button type="submit" className="al-schools-btn" disabled={saving || !editName.trim()}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </NotificationModal>
    </div>
  );
}
